import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { BehaviorSubject, concat, firstValueFrom, from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { StorageService } from 'src/app/storage.service';
import { Course } from 'src/app/views/displayer/courses/courses.dto';
import * as _ from 'underscore';
import { v4 as uuidv4 } from 'uuid';
import { ContentUtilsService } from './content-utils.service';
import { ServerContent } from './content.dto';

let httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  private insertAboveChunk = new BehaviorSubject(null);
  insertAboveChunk$ = this.insertAboveChunk.asObservable();

  private insertBelowChunk = new BehaviorSubject(null);
  insertBelowChunk$ = this.insertBelowChunk.asObservable();

  url;

  constructor(
    private http: HttpClient,
    private env: EnvService,
    private storageService: StorageService,
    private translate: TranslateService,
    private toast: ToastService,
    private envService: EnvService,
    private contentUtilsService: ContentUtilsService,
    private authService: AuthService
  ) {
    this.url = this.env.apiUrl;
  }

  setInsertAboveChunk(id) {
    if (this.insertAboveChunk.value == id) this.insertAboveChunk.next(null);
    else this.insertAboveChunk.next(id);
  }

  setInsertBelowChunk(id) {
    if (this.insertBelowChunk.value == id) this.insertBelowChunk.next(null);
    else this.insertBelowChunk.next(id);
  }

  async getCourses(pageIndex?: number, pageSize?: number, contentType?: string): Promise<Observable<any>> {
    let savedCourseList: any = await this.storageService.find('courseList', [`${pageIndex}_${pageSize}_${contentType}`]);
    this.http.get(this.url + `courses?pageIndex=${pageIndex}&pageSize=${pageSize}&type=${contentType}`, httpOptions).subscribe(async (res: any) => {
      if (pageIndex || pageSize) {
        this.saveCourseList(res.courses ? res.courses : res, pageIndex, pageSize, res.total, contentType);
      }
    });
    return of({ ...(savedCourseList[0] || {}) });
  }

  async getBlogs(pageIndex?, pageSize?): Promise<Observable<any>> {
    let savedblogList: any = await this.storageService.find('blogList', [`${pageIndex}_${pageSize}`]);
    this.http.get(this.url + `blogs/?pageIndex=${pageIndex}&pageSize=${pageSize}`, httpOptions).subscribe((blogList: any) => {
      this.saveBlogList(blogList.docs, pageIndex, pageSize, blogList.total);
    });
    return of({ ...savedblogList[0] });
  }

  getTheCourse(id: string): Observable<Course> {
    const cachedCourse$ = from(this.storageService.find('courses', [id])).pipe(map((cachedResult) => (cachedResult && cachedResult.length > 0 ? cachedResult[0] : null)));

    const freshCourse$ = cachedCourse$.pipe(
      switchMap((cachedCourse) => {
        // Prepare headers. If we have a cached version, send its version number.
        let headers = httpOptions.headers;
        if (cachedCourse?.__v) {
          headers = headers.set('If-None-Match', cachedCourse.__v); // Using ETag/versioning standard
        }

        return this.http.get(this.url + `course/get?courseID=${id}`, { headers, observe: 'response' }).pipe(
          // The API should return 304 NOT MODIFIED if the version is the same. We filter out these responses so no new data is emitted.
          filter((response) => response.status !== 304),
          map((response: any) => this.processCourseData(response.body)),
          tap((processedCourse) => {
            this.storageService.store('courses', processedCourse);
          }),
          catchError((error) => {
            console.error('API fetch error:', error);
            return of(null);
          })
        );
      })
    );

    return concat(cachedCourse$.pipe(filter((course) => course !== null)), freshCourse$.pipe(filter((course) => course !== null)));
  }

  private processCourseData(apiResponse: any): any {
    const hash = {};
    apiResponse.chunks.forEach((chunk) => (hash[chunk?._id] = chunk));

    const course = apiResponse.course;
    course.__name = hash[course._name];
    course.__description = hash[course._description];
    course.sections = course.sections.map((section) => {
      section.__name = hash[section._name];
      section.lectures.forEach((lecture) => (lecture.__name = hash[lecture._name]));
      return section;
    });

    return course;
  }

  async getBlogPost(id): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const savedBlog: any = await this.storageService.find('blogs', [id]);
        var done = false;
        if (savedBlog.length) {
          done = true;
          resolve(savedBlog[0]);
        }
        this.http.get(this.url + `blog/get?id=${id}`, httpOptions).subscribe(async (res: any) => {
          this.storageService.store('blogs', res);
          if (!done) {
            resolve(res);
          }
        });
      } catch (err) {
        console.error(err);
        reject();
      }
    });
  }

  saveBlogList(blogList, pageIndex, pageSize, total): Promise<any> {
    return new Promise((resolve, reject) => {
      const blogListDocument = {
        _id: `${pageIndex}_${pageSize}`,
        docs: blogList,
        pageIndex,
        pageSize,
        total,
      };
      this.storageService.store('blogList', blogListDocument);

      resolve(true);
    });
  }

  saveNotesList(notes, pageIndex, pageSize): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        if (notes.length == 0) {
          resolve(false);
          return;
        }
        const noteData = {
          _id: `${pageIndex}_${pageSize}`,
          ...notes,
        };
        await this.storageService.store('notesList', noteData);
        // this.sendNotesListNotitification(notes);
        resolve(true);
      } catch (err) {
        reject(err);
      }
    });
  }

  saveCourseList(courseList, pageIndex, pageSize, total, contentType): Promise<any> {
    return new Promise((resolve, reject) => {
      const courseListDoc = {
        _id: `${pageIndex}_${pageSize}_${contentType}`,
        courses: courseList,
        total,
      };
      this.storageService.store('courseList', courseListDoc);

      resolve(true);
    });
  }

  async getContent(id, hardReload?: boolean) {
    let _ts = moment().unix();

    let mapChunksToContentThenReslove = (content, chunkIDs, resolve) => {
      this.storageService.find('chunks', chunkIDs).then((chunks: any) => {
        content.contents.forEach((e, i) => {
          let matchedChunk = chunks.find((chunk) => chunk?._id == e._id);
          if (matchedChunk) content.contents[i] = matchedChunk;
        });
        resolve({ content });
      });
    };

    let updateChunksInContentDB = async (chunks: any) => {
      for (const chunk of chunks) {
        chunk._ts = _ts;
        let existingChunk: any = await this.storageService.find('chunks', [chunk?._id]);
        if (existingChunk.length > 0) await this.storageService.update('chunks', chunk?._id, chunk);
        else await this.storageService.store('chunks', chunk);
      }
    };

    return new Promise(async (resolve, reject) => {
      if (hardReload) await this.storageService.remove('contents', [id]);
      let res = (await this.storageService.find('contents', [id])) as any;
      if (res.length == 0) {
        this.http.get(this.url + 'contents/content-info/?id=' + id, httpOptions).subscribe(async (res: any) => {
          if (res.length > 0) {
            res[0]._ts = moment().unix();
            this.storageService.store('contents', res[0]);

            let content = res[0].contents;
            let data = { chunks: content.map((e) => e._id) };

            if (hardReload) {
              this.http.get(this.url + `contents/get-chunks?chunkids=${data.chunks.toString() || null}`, httpOptions).subscribe(async (chunks: any) => {
                await updateChunksInContentDB(chunks);
                mapChunksToContentThenReslove(res[0], data.chunks, resolve);
              });
            } else {
              this.storageService.db['chunks']
                .where('_id')
                .anyOf(data.chunks)
                .keys()
                .then((ids) => {
                  let chunksToSearchFor = _.difference(data.chunks, ids);

                  if (chunksToSearchFor.length == 0) mapChunksToContentThenReslove(res[0], data.chunks, resolve);
                  else {
                    this.http.get(this.url + `contents/get-chunks?chunkids=${chunksToSearchFor.toString() || null}`, httpOptions).subscribe(async (chunks: any) => {
                      await updateChunksInContentDB(chunks);
                      mapChunksToContentThenReslove(res[0], data.chunks, resolve);
                    });
                  }
                });
            }
          } else reject();
        });
      } else {
        let content = res[0].contents;
        let chunkIDs = content.map((e) => e._id);
        let chunks = (await this.storageService.find('chunks', chunkIDs)) as any;

        if (chunks.length == content.length) mapChunksToContentThenReslove(res[0], chunkIDs, resolve);
        else {
          let data = { chunks: content.map((e) => e._id) };
          this.storageService.db['chunks']
            .where('_id')
            .anyOf(data.chunks)
            .keys()
            .then((ids) => {
              let chunksToSearchFor = _.difference(data.chunks, ids);

              this.http.get(this.url + `contents/get-chunks?chunkids=${chunksToSearchFor.toString() || null}`, httpOptions).subscribe(async (chunks: any) => {
                await updateChunksInContentDB(chunks);
                mapChunksToContentThenReslove(res[0], data.chunks, resolve);
              });
            });
        }
      }
    });
  }

  contentEditChunks(data): Observable<any> {
    return this.http.post(this.url + 'contents/edit_chunks', data, httpOptions).pipe(
      tap(async (content: ServerContent) => {
        const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
        this.storageService.store('contents', composedContent);
      }),
      catchError((err) => {
        console.error('Error editing chunks:', err);
        return of(null);
      })
    );
  }

  courseAddContent(data): Observable<any> {
    return this.http.post(this.url + 'course/add-content', data, httpOptions);
  }

  setContentPermissions(data): Observable<any> {
    return this.http.post(this.url + 'contents/set_content_permissions', data, httpOptions);
  }

  getContentPermissions(contentId): Observable<any> {
    return this.http.get(this.url + 'contents/get_content_permissions/' + contentId, httpOptions);
  }

  watchContent(data) {
    const userData = JSON.parse(localStorage.getItem('userData'));
    data.userID = userData.id;
    data.language = this.translate.currentLang;

    const tenantId = localStorage.getItem('active_workspace') || this.envService.appName;
    const config = JSON.parse(localStorage.getItem('configurations') || '{}');
    const projectId = config?.defaultProject?._id || '';
    const evtSource = new EventSource(this.url + `contents/watch-content?${this.toQueryString(data)}&app_name=${this.envService.appName}&tenantId=${tenantId}&projectId=${projectId}`);
    return evtSource;
  }

  toQueryString(params) {
    return Object.keys(params)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
  }

  getAllContent(contentID) {
    return new Promise((resolve, reject) => {
      this.storageService
        .find('contents', [contentID])
        .then((docs) => {
          const content = docs[0];
          const chunkIDs = content.contents.map((element) => element._id);
          this.storageService
            .find('chunks', chunkIDs)
            .then((chunks: any) => {
              const textChunks = chunks.filter((chunk) => chunk.type == 'text' || chunk.type == 'html');
              const editionContents = textChunks.map((chunk) => {
                const edition = chunk.editions.find((edition) => edition.language == this.translate.currentLang) || chunk.editions[0];
                return edition.content;
              });
              const result = editionContents.join(' ');
              resolve(result);
            })
            .catch((err) => {
              console.error(err);
            });
        })
        .catch((err) => {
          console.error(err);
        });
    });
  }

  setContentLinkType(data): Observable<any> {
    return this.http.post(this.url + 'contents/set_link_type/', data, httpOptions);
  }

  deleteAttachment(data): Observable<any> {
    return this.http.post(this.url + 'contents/delete_link_attachment/', data, httpOptions);
  }

  getContentText(contentID): Observable<any> {
    return this.http.get(this.url + 'contents/get-content-text/' + contentID, httpOptions);
  }

  generateContentEmbeddingAction(contentID) {
    let inprogressMessage = this.toast.snackbar.open(this.translate.instant('Snackbar.GeneratingEmbedding'));

    this.generateContentEmbedding({ id: contentID }).subscribe({
      complete: () => {
        inprogressMessage.dismiss();
        this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', {
          duration: 2000,
        });
      },
      error: (err) => {
        inprogressMessage.dismiss();
        this.toast.snackbar.open(this.translate.instant('Snackbar.EmbeddingGenerationFail'), 'Ok', {
          duration: 2000,
        });
      },
    });
  }

  generateEmbeddingsForContentChunks(contentId: string, silent: boolean = false): void {
    this.getContent(contentId, true).then((res: any) => {
      const chunkIds = res.content.contents
        .filter((c) => c.isReadyForIndexing)
        .map((c) => c._id)
        .filter((chunkId) => !chunkId.includes('temp'));

      if (chunkIds.length === 0) {
        if (!silent) this.toast.showWithoutHeader({ level: 'info', text: this.translate.instant('Snackbar.NoIndexingNecessary') });
        return;
      }
      this.http.post(this.url + 'chunks/generate-embeddings-for-ready-chunks', { chunkIds, contentId }, httpOptions).subscribe({
        next: () => {
          if (!silent) this.toast.showWithoutHeader({ level: 'success', text: this.translate.instant('Snackbar.EmbeddingsGenerationInitiated') });
        },
        error: (error) => {
          if (!silent) this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('Snackbar.EmbeddingGenerationFailed') });
          console.error('Error initiating embedding generation:', error);
        },
      });
    });
  }

  generateImageFromContent(contentID, size, style): Observable<any> {
    return this.http.get(this.url + `contents/generate-image-from-content/${contentID}/${size}/${style}`, httpOptions);
  }

  setContentParams(data): Observable<any> {
    return this.http.post(this.url + 'contents/set_params/', data, httpOptions);
  }

  generateContentEmbedding(data): Observable<any> {
    return this.http.post(this.url + 'contents/generate-embedding-for-content/', data, httpOptions);
  }

  deleteContentParam(lectureID): Observable<any> {
    return this.http.get(this.url + 'contents/deleteParams/?lectureID=' + lectureID, httpOptions);
  }

  getContentParams(id): Observable<any> {
    return this.http.get(this.url + 'contents/get_params/?id=' + id, httpOptions);
  }

  getLastChunkUploaded(contentID): Observable<any> {
    return this.http.get(this.url + 'last_chunk_uploaded_in_content/?contentId=' + contentID, httpOptions);
  }

  getContentByName(name): Observable<any> {
    return this.http.get(this.url + 'contents_by_name/?name=' + name, httpOptions);
  }

  contentSetStyle(data): Observable<any> {
    return this.http.post(this.url + 'contents/set-style', data, httpOptions);
  }

  searchChunk(term): Observable<any> {
    return this.http.get(this.url + 'contents/search/' + term, httpOptions);
  }

  searchLecture(term): Observable<any> {
    return this.http.get(this.url + 'contents/searchLecture/' + term, httpOptions);
  }

  searchContent(query, pageIndex, pageSize): Observable<any> {
    return this.http.get(this.url + `search/search-content?query=${query}&pageIndex=${pageIndex}&pageSize=${pageSize}`, httpOptions);
  }

  getAIQuestions(data): Observable<any> {
    return this.http.get(
      this.url + `search/get-ai-questions?page=${data.pageIndex}&pageSize=${data.pageSize}&query=${data.query}&showUnansweredOnly=${data.showUnansweredOnly}`,
      httpOptions
    );
  }

  getSearchHistory(): Observable<any[]> {
    return this.http.get<any[]>(this.url + 'search/history', httpOptions);
  }

  getSearchDetails(id: string): Observable<any> {
    return this.http.get(this.url + 'search/history/' + id, httpOptions);
  }

  deleteSearchHistoryItem(id: string): Observable<any> {
    return this.http.delete(this.url + 'search/history/' + id, httpOptions);
  }

  generateReport(query: string): Observable<any> {
    return this.http.post(this.url + 'analysis/report', { query }, httpOptions);
  }

  getReports(): Observable<any> {
    return this.http.get(this.url + 'analysis/', httpOptions);
  }

  deleteReport(id: string): Observable<any> {
    return this.http.delete(this.url + 'analysis/' + id, httpOptions);
  }

  async searchAI(text, modelName, compoundOptions, includeContext, contextDepth?, includeOutOfContext?, minScore?) {
    const userData = JSON.parse(localStorage.getItem('userData'));
    const uuid = uuidv4();
    const ticketResponse = await firstValueFrom(this.authService.getSseTicket(uuid));
    const ticket = ticketResponse.ticket;

    const tenantId = localStorage.getItem('active_workspace') || this.envService.appName;
    const config = JSON.parse(localStorage.getItem('configurations') || '{}');
    const projectId = config?.defaultProject?._id || '';

    const evtSource = new EventSource(
      this.url +
        `search/AISearch?ticket=${ticket}&query=${text}&language=${this.translate.currentLang}&model=${modelName}&compoundOptions=${compoundOptions}&minScore=${minScore}&includeContext=${includeContext}&contextDepth=${contextDepth}&includeOutOfContext=${includeOutOfContext}&userid=${userData.id}&app_name=${this.envService.appName}&tenantId=${tenantId}&projectId=${projectId}`,
      {}
    );
    return evtSource;
  }

  addMessageToSearchSession(historyId: string, text: string): Observable<any> {
    return this.http.post<any>(this.url + 'search/chat-session/message', { historyId, text }, httpOptions);
  }

  searchVector(text, compoundOptions, minScore?): Observable<any> {
    return this.http.get(this.url + `search/vector?query=${text}&compoundOptions=${compoundOptions}&minScore=${minScore}`, httpOptions);
  }

  tagsAutocomplete(text): Observable<any> {
    return this.http.get(this.url + 'search/tags-autocomplete?query=' + text, httpOptions);
  }

  markQuestionAsAnswered(id): Observable<any> {
    return this.http.get(this.url + `search/mark-response-as-answered?id=${id}`, httpOptions);
  }

  deleteAIAnswer(id) {
    return this.http.delete(this.url + `search/delete-ai-answer?id=${id}`, httpOptions);
  }

  subscribeToNotifications(data): Observable<any> {
    return this.http.post(this.url + 'contents/subscribe-notifications', data, httpOptions);
  }

  unsubscribeToNotifications(data): Observable<any> {
    return this.http.post(this.url + 'contents/unsubscribe-notifications', data, httpOptions);
  }

  sendDataToServiceWorker(data: any): void {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        action: 'unsubscribeToNotifications',
        payload: data,
      });
    } else {
      console.error('No active service worker found to send message');
    }
  }

  editSection(data): Observable<any> {
    return this.http.post(this.url + 'contents/editSectionName', data, httpOptions);
  }

  editLecture(data): Observable<any> {
    return this.http.post(this.url + 'contents/editLectureName', data, httpOptions);
  }

  editCourse(data): Observable<any> {
    return this.http.post(this.url + 'course/edit', data, httpOptions);
  }

  editBlog(data): Observable<any> {
    return this.http.post(this.url + 'blog/edit', data, httpOptions);
  }

  evaluateCourseDuration(courseID): Observable<any> {
    return this.http.get(this.url + 'course/evaluate-course-duration/?courseId=' + courseID, httpOptions);
  }

  courseAddSection(data: { _id: string; sectionName: string; language: string }): Observable<any> {
    return this.http.post(this.url + 'course/add-section', data, httpOptions).pipe(
      tap(async (res: any) => {
        if (res) {
          const processedCourse = this.processCourseData(res);
          await this.storageService.store('courses', processedCourse);
        }
      }),
      catchError((error) => {
        console.error('Error adding section:', error);
        return of(null);
      })
    );
  }

  courseAddLecture(data): Observable<any> {
    return this.http.post(this.url + 'course/add-lecture', data, httpOptions).pipe(
      tap(async (res: { chunk: any; content: any; courseData?: any }) => {
        const { chunk, content, courseData } = res || {};
        if (chunk && content) {
          await this.storageService.store('chunks', chunk);
          const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
          await this.storageService.store('contents', composedContent);
        }
        if (courseData) {
          const processedCourse = this.processCourseData(courseData);
          await this.storageService.store('courses', processedCourse);
        }
      }),
      catchError((error) => {
        console.error('Error adding lecture:', error);
        return of(null);
      })
    );
  }

  moveLecture(data): Observable<any> {
    return this.http.post(this.url + `course/move-lecture`, data, httpOptions);
  }

  updateCourseSettings(id: string, settings: any): Observable<any> {
    return this.http.post(this.url + 'course/update-settings', { id, settings }, httpOptions);
  }

  getUsersCourseRequests(): Observable<any> {
    return this.http.get(this.url + 'course/request-access', httpOptions);
  }

  setSectionLectureOrder(data): Observable<any> {
    return this.http.post(this.url + 'course/set-section-lecture-order', data, httpOptions);
  }

  syncHierarchy(data: { courseID: string; sections: any[]; lectures: any[] }): Observable<any> {
    return this.http.post(this.url + 'course/sync-hierarchy', data, httpOptions);
  }

  getCourseUsers(): Observable<any> {
    return this.http.get(this.url + 'course/get-courses-users', httpOptions);
  }

  requestCourseAccess(data): Observable<any> {
    return this.http.post(this.url + 'course/set-request-access', data, httpOptions);
  }

  createCourse(data): Observable<any> {
    return this.http.post(this.url + 'course/create', data, httpOptions).pipe(
      tap(async (res: { course: any; chunk: any; content: any }) => {
        const { course, chunk, content } = res || {};
        if (course && chunk && content) {
          await this.storageService.store('chunks', chunk);
          const composedContent = await this.contentUtilsService.composeContentWithChunks(content);
          console.log('Composed content for new course:', composedContent);
          await this.storageService.store('contents', composedContent);
        }
      })
    );
  }

  generateCoursePlan(data: { subject: string; description: string; language: string; instructions?: string }): Observable<any> {
    return this.http.post(this.url + 'course/generate-plan', data, httpOptions);
  }

  generateSectionPlan(data: { courseName: string; sectionTitle: string; language: string; instructions?: string }): Observable<any> {
    return this.http.post(this.url + 'course/generate-section-plan', data, httpOptions);
  }

  createBlog(data): Observable<any> {
    return this.http.post(this.url + 'blog/create', data, httpOptions);
  }

  deleteResource(cID, lec, sec): Observable<any> {
    return this.http.post(this.url + 'delete_resource/', { courseID: cID, lecture: lec, section: sec }, httpOptions);
  }

  getAllCourses(): Observable<any> {
    return this.http.get(this.url + 'all-courses/', httpOptions);
  }

  searchCoursesByName(text): Observable<any> {
    return this.http.get(this.url + 'course/searchCoursesByName/?text=' + text, httpOptions);
  }
  searchCoursesById(id): Observable<any> {
    return this.http.get(this.url + 'course/searchCoursesById/?id=' + id, httpOptions);
  }

  getContentList(type: string, pageIndex?: number, pageSize?: number): Observable<any> {
    return this.http.get(this.url + `content-list?type=${type}&pageIndex=${pageIndex}&pageSize=${pageSize}`, httpOptions);
  }
  testAudio(): Observable<any> {
    return this.http.get(this.url + 'test_audio/', httpOptions);
  }

  submitChunkUpload(data): Observable<any> {
    return this.http.post(this.url + 'submit_chunk_file', data, httpOptions);
  }



  downloadAsArrayBuffer(url): Observable<any> {
    return this.http.get(url, { responseType: 'arraybuffer' });
  }

  getChunkAudioLink(data): Observable<any> {
    return this.http.get(this.url + 'get_audio_link/?data=' + data, {
      headers: new HttpHeaders({
        'Content-Type': 'application/octet-stream', //,
      }),
    });
  }
}
